import React, { useState } from "react";
import { createPortal } from "react-dom";
import { X, Scissors } from "lucide-react";

// Reproduce en HTML el mismo contenido/orden que arma print-bridge.js (buildComandaTicket
// / buildInvoiceTicket) para que se pueda revisar el formato del ticket sin tener que
// imprimir físicamente cada vez que se ajusta algo. No es una réplica pixel-a-pixel del
// ESC/POS real (eso solo se ve en el papel), pero sí el mismo contenido y el mismo orden,
// incluyendo el logo + espacio antes del corte que arregla el ticket cortado.
const SAMPLE_COMANDA = {
  stationName: "Cocina",
  tableLabel: "Mesa 4",
  roundNumber: 2,
  createdAt: new Date().toISOString(),
  items: [
    { quantity: 2, title: "Hamburguesa clásica", note: "sin cebolla" },
    { quantity: 1, title: "Papas francesas" },
    { quantity: 3, title: "Limonada natural" },
  ],
};

const SAMPLE_INVOICE = {
  companyName: "Mi Restaurante",
  orderNumber: "#Q-10234",
  createdAt: new Date().toISOString(),
  customerLabel: "Juan Pérez",
  mesero: "Carla G.",
  placeLabel: "Mesa 4",
  items: [
    { quantity: 2, title: "Hamburguesa clásica", price: 18000 },
    { quantity: 1, title: "Papas francesas", price: 9000 },
    { quantity: 3, title: "Limonada natural", price: 6000 },
  ],
  subtotal: 63000,
  total: 63000,
  paymentMethod: "efectivo",
};

const money = (n) => `$${Number(n || 0).toLocaleString("es-CO")}`;

function ReceiptShell({ children }) {
  return (
    <div className="mx-auto bg-white text-black font-mono text-[11px] leading-snug w-[280px] shadow-lg"
      style={{
        clipPath: "polygon(0% 2%,4% 0%,8% 2%,12% 0%,16% 2%,20% 0%,24% 2%,28% 0%,32% 2%,36% 0%,40% 2%,44% 0%,48% 2%,52% 0%,56% 2%,60% 0%,64% 2%,68% 0%,72% 2%,76% 0%,80% 2%,84% 0%,88% 2%,92% 0%,96% 2%,100% 0%,100% 98%,96% 100%,92% 98%,88% 100%,84% 98%,80% 100%,76% 98%,72% 100%,68% 98%,64% 100%,60% 98%,56% 100%,52% 98%,48% 100%,44% 98%,40% 100%,36% 98%,32% 100%,28% 98%,24% 100%,20% 98%,16% 100%,12% 98%,8% 100%,4% 98%,0% 100%)",
      }}
    >
      <div className="px-4 py-5">{children}</div>
    </div>
  );
}

function Hr() {
  return <div className="border-t border-dashed border-black my-1.5" />;
}

function Footer() {
  return (
    <>
      <div className="flex flex-col items-center mt-3">
        <img src="/logo192.png" alt="Qkiere" className="w-10 h-10 grayscale contrast-150 opacity-90" />
        <p className="mt-1 font-bold tracking-wide">Qkiere.com</p>
      </div>
      {/* Este espacio en blanco es a propósito — es el margen que antes faltaba y
          hacía que la cuchilla de la impresora cortara justo sobre "Qkiere.com". */}
      <div className="h-8" />
      <div className="flex items-center gap-2 text-gray-400 border-t border-dashed border-gray-300 pt-1.5">
        <Scissors size={11} />
        <span className="text-[9px]">línea de corte — antes caía encima del texto</span>
      </div>
    </>
  );
}

function ComandaPreview() {
  const job = SAMPLE_COMANDA;
  return (
    <ReceiptShell>
      <p className="text-center font-extrabold uppercase">{job.stationName}</p>
      <Hr />
      <p className="font-extrabold">{job.tableLabel}  Ronda {job.roundNumber}</p>
      <p>{new Date(job.createdAt).toLocaleString("es-CO")}</p>
      <Hr />
      {job.items.map((it, i) => (
        <div key={i}>
          <p>{it.quantity}x {it.title}</p>
          {it.note && <p className="pl-3 text-gray-600">({it.note})</p>}
        </div>
      ))}
      <Footer />
    </ReceiptShell>
  );
}

function InvoicePreview() {
  const job = SAMPLE_INVOICE;
  return (
    <ReceiptShell>
      <p className="text-center font-extrabold uppercase">{job.companyName}</p>
      <p className="text-center">Factura de venta</p>
      <Hr />
      <p>{job.orderNumber}</p>
      <p>{new Date(job.createdAt).toLocaleString("es-CO")}</p>
      <p>Cliente: {job.customerLabel}</p>
      <p>Atendido por: {job.mesero}</p>
      <p>{job.placeLabel}</p>
      <Hr />
      {job.items.map((it, i) => (
        <div key={i} className="mb-0.5">
          <p>{it.quantity}x {it.title}</p>
          <div className="flex justify-between">
            <span className="pl-3 text-gray-600">{money(it.price)} c/u</span>
            <span>{money(it.price * it.quantity)}</span>
          </div>
        </div>
      ))}
      <Hr />
      <div className="flex justify-between"><span>Subtotal</span><span>{money(job.subtotal)}</span></div>
      <div className="flex justify-between font-extrabold"><span>TOTAL</span><span>{money(job.total)}</span></div>
      <p>Pago: {job.paymentMethod}</p>
      <Hr />
      <Footer />
    </ReceiptShell>
  );
}

export default function TicketPreviewModal({ onClose }) {
  const [tab, setTab] = useState("comanda");

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-gray-100 rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 bg-white border-b border-gray-200">
          <h3 className="font-bold text-gray-800">Vista previa del ticket</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
        </div>

        <div className="flex gap-2 px-5 pt-4">
          <button
            type="button"
            onClick={() => setTab("comanda")}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${tab === "comanda" ? "bg-supernova-500 text-white" : "bg-white border border-gray-200 text-gray-600"}`}
          >
            Comanda (cocina)
          </button>
          <button
            type="button"
            onClick={() => setTab("factura")}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${tab === "factura" ? "bg-supernova-500 text-white" : "bg-white border border-gray-200 text-gray-600"}`}
          >
            Factura de venta
          </button>
        </div>

        <div className="overflow-y-auto py-6 px-4">
          {tab === "comanda" ? <ComandaPreview /> : <InvoicePreview />}
        </div>

        <p className="text-xs text-gray-400 text-center px-5 pb-4">
          Datos de ejemplo — el contenido real varía según la mesa/pedido. El ancho exacto
          depende del papel de tu impresora (48 u 80mm).
        </p>
      </div>
    </div>,
    document.body,
  );
}
